import React, { useMemo } from "react";
import moment from "moment-timezone";
import VitalCard from "./VitalCard";
import "./CustomerVitalsStrip.css";

/**
 * Credit Balance: the same source as the Session Credit Summary table,
 * GET /api/staff/customers/:id/credit-summary (totals.creditsLeft). The old
 * calculation added fields that do not exist (saunaCredits,
 * redLightCredits, massageCredits) and missed memberships, packages, and
 * drop-ins, so it showed 0 for most members.
 *
 * @param {Object|null} creditSummary
 * @returns {number|null} null while the summary has not loaded
 */
export const creditBalanceFromSummary = (creditSummary) => {
  if (!creditSummary || !creditSummary.totals) return null;
  return Number(creditSummary.totals.creditsLeft) || 0;
};

/**
 * Format a relative date label like "Today", "Yesterday", "3 days ago",
 * "2 weeks ago", "1 month ago". Uses moment to keep the date-fns dep
 * optional; this codebase already imports moment-timezone everywhere.
 *
 * @param {string|Date|null|undefined} dateInput
 * @returns {string|null}  Returns null if date is invalid/missing.
 */
export const formatRelativeVisit = (dateInput) => {
  if (!dateInput) return null;
  const m = moment(dateInput);
  if (!m.isValid()) return null;
  const now = moment();
  const startOfToday = now.clone().startOf("day");
  const startOfDate = m.clone().startOf("day");
  const dayDiff = startOfToday.diff(startOfDate, "days");

  if (dayDiff === 0) return "Today";
  if (dayDiff === 1) return "Yesterday";
  if (dayDiff > 1 && dayDiff < 7) return `${dayDiff} days ago`;
  if (dayDiff >= 7 && dayDiff < 30) {
    const weeks = Math.floor(dayDiff / 7);
    return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
  }
  if (dayDiff >= 30 && dayDiff < 365) {
    const months = Math.floor(dayDiff / 30);
    return months === 1 ? "1 month ago" : `${months} months ago`;
  }
  const years = Math.floor(dayDiff / 365);
  return years === 1 ? "1 year ago" : `${years} years ago`;
};

/**
 * Format the absolute MST helper, e.g. "Apr 30, 2026 · 2:14 PM MST".
 */
export const formatAbsoluteMst = (dateInput) => {
  if (!dateInput) return "";
  const m = moment.tz(dateInput, "America/Phoenix");
  if (!m.isValid()) return "";
  return `${m.format("MMM D, YYYY · h:mm A")} MST`;
};

/**
 * Format dollar amount with thousands separator. No cents shown unless
 * < $100, in which case 2 decimals.
 *
 * @param {number} amount
 * @returns {string}
 */
export const formatLifetimeSpend = (amount) => {
  const num = Number(amount) || 0;
  if (num < 100) {
    return `$${num.toFixed(2)}`;
  }
  return `$${Math.round(num).toLocaleString("en-US")}`;
};

/**
 * Format a "since {memberSince}" helper, e.g. "since Mar 2024".
 */
export const formatMemberSince = (createdAt) => {
  if (!createdAt) return "";
  const m = moment(createdAt);
  if (!m.isValid()) return "";
  return `since ${m.format("MMM YYYY")}`;
};

/**
 * CustomerVitalsStrip — 4-card at-a-glance row above the tab bar on the
 * customer detail page.
 *
 * Per CUST-DETAIL-POLISH-002 design spec.
 *
 * @param {Object} props
 * @param {Object|null} props.customer        Customer DTO (or null while loading).
 *   Uses the computed fields lastVisitAt and lifetimeSpend.
 * @param {Object|null} [props.creditSummary] Credit summary DTO (credit table source)
 * @param {Object|null} [props.bookingStats]  Booking-stats DTO from
 *   `GET /api/staff/customers/:id/booking-stats`. Source of truth for
 *   Lifetime Visits per spec §3.4 ("status === 'completed' only"). Falls
 *   back to 0 if absent (BUG-104 fix).
 * @param {boolean} [props.loading=false]     Initial-fetch flag (skeleton mode)
 * @param {boolean} [props.error=false]       Customer-detail fetch error (per-card error treatment)
 * @param {Function} [props.onActivateTab]    (tabKey) => void; called by clickable cards
 */
const CustomerVitalsStrip = ({
  customer,
  creditSummary = null,
  bookingStats = null,
  loading = false,
  error = false,
  onActivateTab,
}) => {
  const creditBalance = useMemo(
    () => creditBalanceFromSummary(creditSummary),
    [creditSummary]
  );

  if (loading) {
    return (
      <div className="vitals-strip" data-testid="vitals-strip">
        <VitalCard label="Credit Balance" loading accent="gold" />
        <VitalCard label="Last Visit" loading />
        <VitalCard label="Lifetime Visits" loading />
        <VitalCard label="Lifetime Spend" loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="vitals-strip" data-testid="vitals-strip">
        <VitalCard label="Credit Balance" error="fetch-failed" accent="gold" />
        <VitalCard label="Last Visit" error="fetch-failed" />
        <VitalCard label="Lifetime Visits" error="fetch-failed" />
        <VitalCard label="Lifetime Spend" error="fetch-failed" />
      </div>
    );
  }

  // Defensive — caller should have either passed loading/error or a customer
  if (!customer) return null;

  // ---- Credit Balance ----
  const creditLoading = creditBalance === null;
  const creditEmpty = creditBalance === 0;
  const creditValue = creditLoading ? "…" : String(creditBalance);
  const creditHelper = creditLoading
    ? "loading credits"
    : creditEmpty
    ? "no active credits"
    : creditBalance === 1
    ? "credit remaining"
    : "credits remaining";

  // ---- Last Visit ----
  // lastVisitAt: the latest completed booking (computed by the server).
  const relativeVisit = formatRelativeVisit(customer.lastVisitAt);
  const lastVisitEmpty = !relativeVisit;
  const lastVisitValue = lastVisitEmpty ? "Never" : relativeVisit;
  const lastVisitHelper = lastVisitEmpty
    ? "first-time customer"
    : formatAbsoluteMst(customer.lastVisitAt);

  // ---- Lifetime Visits ----
  // BUG-104 fix: must reflect ONLY completed bookings per Zuri spec §3.4
  // ("integer count of bookings with status === 'completed'; NOT no-shows
  // or cancelled"). `customer.totalBookings` is unfiltered and would
  // inflate the number to staff. Source of truth is the booking-stats
  // endpoint's `completedBookings` field. If the stats fetch hasn't
  // resolved yet (or failed) we render 0 ("no completed visits") rather
  // than the inflated count — same UX as a first-time customer, no
  // misleading data. The skeleton state is owned by the `loading` prop.
  const completedVisits = Number(bookingStats?.completedBookings) || 0;
  const lifetimeVisitsEmpty = completedVisits === 0;
  const lifetimeVisitsValue = lifetimeVisitsEmpty
    ? "0"
    : completedVisits.toLocaleString("en-US");
  const lifetimeVisitsHelper = lifetimeVisitsEmpty
    ? "no completed visits"
    : formatMemberSince(customer.createdAt);

  // ---- Lifetime Spend ----
  // lifetimeSpend: successful payments minus refunds (computed by the
  // server; includes POS and corporate-rate purchases).
  const lifetimeValue = Number(customer.lifetimeSpend) || 0;
  const lifetimeSpendEmpty = lifetimeValue === 0;
  const lifetimeSpendValue = lifetimeSpendEmpty
    ? "$0"
    : formatLifetimeSpend(lifetimeValue);
  const lifetimeSpendHelper = lifetimeSpendEmpty
    ? "no payments recorded"
    : "total revenue from this customer";

  const handleActivate = (tabKey) => () => {
    if (typeof onActivateTab === "function") onActivateTab(tabKey);
  };

  return (
    <div className="vitals-strip" data-testid="vitals-strip">
      <VitalCard
        label="Credit Balance"
        value={creditValue}
        helper={creditHelper}
        accent="gold"
        empty={creditEmpty || creditLoading}
        onClick={
          creditEmpty || creditLoading ? undefined : handleActivate("memberships")
        }
        targetTab="Session Credits tab"
      />
      <VitalCard
        label="Last Visit"
        value={lastVisitValue}
        helper={lastVisitHelper}
        empty={lastVisitEmpty}
        onClick={lastVisitEmpty ? undefined : handleActivate("bookings")}
        targetTab="Bookings tab"
      />
      <VitalCard
        label="Lifetime Visits"
        value={lifetimeVisitsValue}
        helper={lifetimeVisitsHelper}
        empty={lifetimeVisitsEmpty}
        onClick={lifetimeVisitsEmpty ? undefined : handleActivate("bookings")}
        targetTab="Bookings tab"
      />
      <VitalCard
        label="Lifetime Spend"
        value={lifetimeSpendValue}
        helper={lifetimeSpendHelper}
        empty={lifetimeSpendEmpty}
        onClick={lifetimeSpendEmpty ? undefined : handleActivate("payments")}
        targetTab="Payment Methods tab"
      />
    </div>
  );
};

export default CustomerVitalsStrip;
